import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-warm-400 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-warm-800 text-white shadow-sm hover:bg-warm-900 active:bg-warm-900',
        destructive: 'bg-solution text-white shadow-sm hover:bg-solution-dark active:bg-solution-dark',
        outline: 'border border-border bg-surface text-text shadow-sm hover:bg-warm-50 hover:border-warm-400',
        secondary: 'bg-warm-100 text-text-secondary shadow-sm hover:bg-warm-200 hover:text-text',
        ghost: 'text-text-secondary hover:bg-warm-100 hover:text-text',
        link: 'text-warm-700 underline-offset-4 hover:underline',
        market: 'bg-market text-white shadow-sm hover:bg-market-dark active:bg-market-dark',
        business: 'bg-business text-white shadow-sm hover:bg-business-dark active:bg-business-dark',
        solution: 'bg-solution text-white shadow-sm hover:bg-solution-dark active:bg-solution-dark',
      },
      size: {
        default: 'h-10 px-5 py-2',
        sm: 'h-8 rounded-md px-3.5 text-xs',
        lg: 'h-11 rounded-lg px-8 text-base',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, ...props }, ref) => {
    return (
      <button
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = 'Button'

export { Button, buttonVariants }
